import { addDays } from '@felix/domain';
import type { TFunction } from 'i18next';
import { descendantIds, type OverviewCounts } from './equipment-tree.js';
import { workOrderStatus } from './status-tone.js';
import type {
  CalendarUnit,
  EquipmentNode,
  HistoryEntry,
  JobList,
  JobView,
  PlanItem,
  WorkOrderView,
} from './types.js';

/**
 * PMS iş ekranlarının saf hesapları (plan M7 Task 14a–14d): sıklık metni, plan aralıkları, takvim
 * noktaları, Overview satırları ve sayıları, iş emri adımı, sıralama ve sayfalama, History
 * süzgeç seçenekleri ve "Eylem × uygulama" kuralları. Sayılar ve durumlar koddan (ADR 0024 #19).
 */

/* ---------- Sıklık ---------- */

export interface IntervalLike {
  calendar?: { every: number; unit: CalendarUnit } | null;
  runningHours?: number | null;
}

const SINGLE: Readonly<Record<CalendarUnit, string>> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
  year: 'annual',
};

export function numberFormat(language: string, value: number): string {
  return new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-GB', {
    maximumFractionDigits: 1,
  }).format(value);
}

/** "Daily", "Every 3 months", "2,000 h", "Monthly / 500 h"; yalnız durum tetikli "Condition based". */
export function frequencyLabel(interval: IntervalLike, t: TFunction, language: string): string {
  const parts: string[] = [];
  const calendar = interval.calendar ?? null;
  if (calendar) {
    parts.push(
      calendar.every === 1
        ? t(`pms.work.frequency.${SINGLE[calendar.unit]}`)
        : t(`pms.work.frequency.every.${calendar.unit}`, { count: calendar.every }),
    );
  }
  const hours = interval.runningHours ?? null;
  if (hours !== null) {
    parts.push(t('pms.work.frequency.hours', { hours: numberFormat(language, hours) }));
  }
  return parts.length > 0 ? parts.join(' / ') : t('pms.work.frequency.condition');
}

/* ---------- Plan aralıkları ---------- */

export interface DayRange {
  from?: string;
  to?: string;
}

/** Ayın ilk ve son günü (`YYYY-MM`). */
export function monthRange(month: string): { from: string; to: string } {
  const [year, value] = month.split('-').map(Number);
  const last = new Date(Date.UTC(year ?? 2000, value ?? 1, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** Günün haftası: pazartesiden pazara (takvim ızgarasıyla aynı). */
export function weekRange(day: string): { from: string; to: string } {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  const from = addDays(day, -((weekday + 6) % 7));
  return { from, to: addDays(from, 6) };
}

const dueOf = (plan: PlanItem) => plan.due.dueOn;

/**
 * Aralıktaki işler, vadeye göre: gecikmiş iş aralığın başından önce de kalır (gecikme gizlenmez;
 * sunucudaki `vesselPlan` ile aynı kural). Aralık yoksa hepsi; vadesi olmayan sonda.
 */
export function plansIn(plans: readonly PlanItem[], range: DayRange): PlanItem[] {
  return plans
    .filter((item) => {
      const due = dueOf(item);
      if (item.status === 'overdue') return range.to === undefined || (due ?? '') <= range.to;
      if (due === undefined) return range.from === undefined && range.to === undefined;
      return (
        (range.from === undefined || due >= range.from) &&
        (range.to === undefined || due <= range.to)
      );
    })
    .sort(
      (a, b) =>
        (dueOf(a) ?? '9999').localeCompare(dueOf(b) ?? '9999') || a.code.localeCompare(b.code),
    );
}

/** "Upcoming Tasks (Next 7 Days)": vadesi bugünle yedi gün sonrası arasında, gecikmemiş. */
export function upcoming(plans: readonly PlanItem[], today: string, days = 7): PlanItem[] {
  const last = addDays(today, days);
  return plansIn(
    plans.filter((p) => p.status !== 'overdue'),
    { from: today, to: last },
  );
}

/* ---------- Takvim ---------- */

export type CalendarTone = 'info' | 'warning' | 'danger' | 'success';
const TONE_ORDER: readonly CalendarTone[] = ['info', 'warning', 'danger', 'success'];

/**
 * PMS Calendar noktaları (lejant sırası): planlı vade "Scheduled" (mavi), gecikmiş vade "Overdue"
 * (kırmızı), süren iş emri "In Progress" (turuncu), ayda yapılmış iş "Completed" (yeşil). İptal
 * edilmiş kayıt sayılmaz.
 */
export function calendarMarks(input: {
  month: string;
  plans: readonly PlanItem[];
  workOrders: readonly WorkOrderView[];
  history: readonly HistoryEntry[];
  /**
   * "Show only" süzgeci seçiliyken yalnız bu işlerin iş emri ve yapılış noktaları (ortak
   * 2026-10-25): takvim tabloyla ve "Upcoming Tasks"la aynı işleri gösterir. Verilmezse hepsi.
   */
  jobIds?: ReadonlySet<string>;
}): Map<string, CalendarTone[]> {
  const marks = new Map<string, Set<CalendarTone>>();
  const add = (day: string | null | undefined, tone: CalendarTone) => {
    if (!day || day.slice(0, 7) !== input.month) return;
    const set = marks.get(day) ?? new Set<CalendarTone>();
    set.add(tone);
    marks.set(day, set);
  };
  const shown = (jobId: string | null) =>
    input.jobIds === undefined || (jobId !== null && input.jobIds.has(jobId));
  for (const plan of input.plans)
    add(plan.due.dueOn, plan.status === 'overdue' ? 'danger' : 'info');
  for (const wo of input.workOrders) {
    if (shown(wo.jobId) && workOrderStatus(wo) === 'in_progress') {
      add(workOrderDueDate(wo), 'warning');
    }
  }
  for (const record of input.history) {
    if (shown(record.jobId) && record.status !== 'voided') add(record.doneOn, 'success');
  }
  return new Map(
    [...marks.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, set]) => [day, TONE_ORDER.filter((tone) => set.has(tone))]),
  );
}

/* ---------- Equipment Overview ---------- */

/** Seçilen ve bütün alt düğümleri (Equipment List panelleri seçime göre süzülür). */
export function descendantSet(nodes: readonly EquipmentNode[], id: string): Set<string> {
  return new Set([id, ...descendantIds(nodes, id)]);
}

const subtree = descendantSet;

export interface OverviewRow {
  job: JobView;
  plan: PlanItem | null;
}

/** Seçilenin alt ağacındaki işler, ağaç sırasıyla (bileşen) ve koda göre. */
export function overviewRows(
  nodes: readonly EquipmentNode[],
  selectedId: string,
  jobs: readonly JobView[],
  plans: readonly PlanItem[],
): OverviewRow[] {
  const ids = subtree(nodes, selectedId);
  const order = new Map([selectedId, ...descendantIds(nodes, selectedId)].map((id, i) => [id, i]));
  const byJob = new Map(plans.map((p) => [p.jobId, p]));
  return jobs
    .filter((job) => ids.has(job.equipmentId) && !job.deletedAt)
    .sort(
      (a, b) =>
        (order.get(a.equipmentId) ?? 0) - (order.get(b.equipmentId) ?? 0) ||
        String(a.code).localeCompare(String(b.code)),
    )
    .map((job) => ({ job, plan: byJob.get(job.id) ?? null }));
}

/**
 * Overview sayıları: bileşen sayısı ağaçtan; "PM Tasks (Due)" vadesi yaklaşan, "Overdue" gecikmiş
 * işler; "Completed (30 Days)" son 30 günde yapılmış ve iptal edilmemiş kayıtlar.
 */
export function overviewSummary(
  nodes: readonly EquipmentNode[],
  selectedId: string,
  plans: readonly PlanItem[],
  history: readonly HistoryEntry[],
  today: string,
): OverviewCounts {
  const ids = subtree(nodes, selectedId);
  const mine = plans.filter((p) => ids.has(p.equipmentId));
  const since = addDays(today, -29);
  return {
    totalComponents: ids.size - 1,
    due: mine.filter((p) => p.status === 'due_soon').length,
    overdue: mine.filter((p) => p.status === 'overdue').length,
    completed: history.filter(
      (h) =>
        ids.has(h.equipmentId) && h.status !== 'voided' && h.doneOn >= since && h.doneOn <= today,
    ).length,
  };
}

/* ---------- Eylem × uygulama (plan M7 "Eylem × uygulama"; K1, K11) ---------- */

export interface JobActions {
  create: boolean;
  edit: boolean;
  /** Kayıt yokken aralık gerekçeyle doğrudan düzeltilir (K11). */
  intervalDirect: boolean;
  /** Kayıttan sonra "Request interval change" (yalnız gemide). */
  requestInterval: boolean;
  /** Canlı gemide kayıtlı işin aralığını ofis girer: "Change interval" (yalnız web; ADR 0104). */
  officeInterval: boolean;
  /** Ofiste etkinleşmiş gemi: salt okunur ve "Edited on board" (K1). */
  editedOnBoard: boolean;
}

/**
 * Ofiste "Edited on board": yalnız kurulum bilgisi göndermeyen eski sunucuda etkin gemi (K1).
 * Etkin gemide ofis yazar, değişiklik gemiye gider (ADR 0142 §3, Ek 2026-10-31): uyarı yok.
 */
export function editedOnBoardOf(
  scope: 'web' | 'ship',
  list: { writer?: 'cloud' | 'ship'; setup?: { mode: string } } | null,
): boolean {
  return scope === 'web' && list?.writer === 'ship' && list.setup?.mode !== 'queued';
}

export function jobActions(
  scope: 'web' | 'ship',
  list: Pick<JobList, 'writer' | 'can' | 'setup'> | null,
  hasRecord: boolean,
): JobActions {
  const editedOnBoard = editedOnBoardOf(scope, list);
  const writable = !editedOnBoard && list !== null;
  const edit = writable && list.can.edit;
  return {
    create: writable && list.can.create,
    edit,
    intervalDirect: edit && !hasRecord,
    requestInterval:
      scope === 'ship' && hasRecord && list !== null && list.can.intervalChange === true,
    // Kayıtsız işin aralığı düzenlemeyle değişir; kayıtlıda ofis kararı (ADR 0104)
    officeInterval:
      scope === 'web' &&
      list?.writer === 'ship' &&
      list.can.intervalChange === true &&
      !(edit && !hasRecord),
    editedOnBoard,
  };
}

/* ---------- İş emirleri ---------- */

export const WORK_ORDER_STEPS = [
  'assigned',
  'started',
  'safetyCheck',
  'maintenance',
  'measurements',
  'spareParts',
  'photos',
  'completed',
  'approval',
  'closed',
] as const;
export type WorkOrderStepKey = (typeof WORK_ORDER_STEPS)[number];

/**
 * İş emrinin bulunduğu adım (PMS belgesi §12): raporlanmışsa onay ya da kapalı; başlamışsa
 * güvenlik kontrolü bitene kadar "Safety Check", sonra "Maintenance"; değilse "Assigned".
 */
export function workOrderStep(
  wo: Pick<WorkOrderView, 'status' | 'record' | 'startedAt' | 'safetyCheck'>,
): number {
  const at = (key: WorkOrderStepKey) => WORK_ORDER_STEPS.indexOf(key);
  if (wo.record) return at(wo.record.status === 'approved' ? 'closed' : 'approval');
  if (wo.status === 'in_progress' || wo.startedAt) {
    const items = wo.safetyCheck?.items ?? [];
    return at(items.length > 0 && items.every((i) => i.checked) ? 'maintenance' : 'safetyCheck');
  }
  return at('assigned');
}

/**
 * İşin açık iş emri (ortak 2026-10-25): iptal edilmemiş ve raporlanmamış (sunucudaki
 * `openWorkOrders` ile aynı kural); birden çoksa ilk açılan. Kısayol yeni iş emri açmak yerine
 * buna götürür.
 */
export function openWorkOrderFor(
  workOrders: readonly WorkOrderView[],
  jobId: string,
): WorkOrderView | null {
  const open = workOrders
    .filter((wo) => wo.jobId === jobId && wo.status !== 'cancelled' && wo.record === null)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.woNo.localeCompare(b.woNo));
  return open[0] ?? null;
}

/** Due Date: planlı işte plan motorundan (K39), diğerlerinde hedef tarih. */
export function workOrderDueDate(wo: Pick<WorkOrderView, 'due' | 'targetOn'>): string | null {
  return wo.due.dueOn ?? wo.due.targetOn ?? wo.targetOn ?? null;
}

export type SortDir = 'asc' | 'desc';
export interface Sort<K extends string> {
  key: K;
  dir: SortDir;
}

const PRIORITY_RANK: Readonly<Record<string, number>> = { low: 1, medium: 2, high: 3 };

function compare(a: string | number | null, b: string | number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b));
}

function sorted<T, K extends string>(
  items: readonly T[],
  sort: Sort<K>,
  value: (item: T, key: K) => string | number | null,
  tie: (item: T) => string,
): T[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = value(a, sort.key);
    const vb = value(b, sort.key);
    // Boş değer yöne bakmadan sonda
    if (va === null || vb === null) return compare(va, vb) || tie(a).localeCompare(tie(b));
    return sign * compare(va, vb) || tie(a).localeCompare(tie(b));
  });
}

export type WorkOrderSortKey =
  | 'woNo'
  | 'component'
  | 'task'
  | 'priority'
  | 'status'
  | 'dueDate'
  | 'assignedTo';

export function sortWorkOrders(
  items: readonly WorkOrderView[],
  sort: Sort<WorkOrderSortKey>,
): WorkOrderView[] {
  return sorted(
    items,
    sort,
    (wo, key) => {
      switch (key) {
        case 'woNo':
          return wo.woNo;
        case 'component':
          return wo.equipmentName ?? wo.equipmentCode;
        case 'task':
          return wo.workType ?? wo.title;
        case 'priority':
          return PRIORITY_RANK[wo.priority] ?? 0;
        case 'status':
          return workOrderStatus(wo);
        case 'dueDate':
          return workOrderDueDate(wo);
        case 'assignedTo':
          return wo.assignedToName;
      }
    },
    (wo) => wo.woNo,
  );
}

/** Arama: numara, bileşen, iş ya da atanan. */
export function searchWorkOrders(items: readonly WorkOrderView[], query: string): WorkOrderView[] {
  const needle = query.trim().toLocaleLowerCase('en');
  if (needle === '') return [...items];
  return items.filter((wo) =>
    [wo.woNo, wo.equipmentName, wo.equipmentCode, wo.title, wo.workType, wo.assignedToName]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLocaleLowerCase('en').includes(needle)),
  );
}

export interface Page<T> {
  rows: T[];
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
}

export function paginate<T>(items: readonly T[], page: number, size: number): Page<T> {
  const pages = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * size;
  const rows = items.slice(start, start + size);
  return {
    rows,
    page: current,
    pages,
    from: rows.length === 0 ? 0 : start + 1,
    to: start + rows.length,
    total: items.length,
  };
}

/* ---------- History ---------- */

export type HistorySortKey = 'doneOn' | 'woNo' | 'equipment' | 'job' | 'performedBy' | 'status';

export function performerNames(entry: Pick<HistoryEntry, 'performedBy'>): string {
  return (entry.performedBy ?? []).map((p) => p.name).join(', ');
}

export function sortHistory(
  entries: readonly HistoryEntry[],
  sort: Sort<HistorySortKey>,
): HistoryEntry[] {
  return sorted(
    entries,
    sort,
    (entry, key) => {
      switch (key) {
        case 'doneOn':
          return entry.doneAt;
        case 'woNo':
          return entry.woNo;
        case 'equipment':
          return entry.equipmentName ?? entry.equipmentCode;
        case 'job':
          return entry.jobCode ?? entry.jobTitle;
        case 'performedBy':
          return performerNames(entry);
        case 'status':
          return entry.status;
      }
    },
    (entry) => `${entry.createdAt}|${entry.id}`,
  );
}

/** Uygulayıcı süzgeci: adında geçen (büyük-küçük harf duyarsız). */
export function filterByPerformer(entries: readonly HistoryEntry[], text: string): HistoryEntry[] {
  const needle = text.trim().toLocaleLowerCase('en');
  if (needle === '') return [...entries];
  return entries.filter((e) => performerNames(e).toLocaleLowerCase('en').includes(needle));
}

export interface FilterOption {
  value: string;
  label: string;
  /** Kayıttan kaldırılmış ekipman ya da silinmiş iş tanımı (geçmişi görünür). */
  deleted: boolean;
}

/**
 * History süzgeç seçenekleri [NR496 4.3.2a]: ağaçtaki ekipman ve kütüphanedeki işlerin yanında
 * kayıtlarda geçen silinmiş ekipman ve iş tanımı da seçilir; geçmişleri kaybolmaz.
 */
export function historyOptions(
  entries: readonly HistoryEntry[],
  nodes: readonly EquipmentNode[],
  jobs: readonly Pick<JobView, 'id' | 'code' | 'title' | 'deletedAt'>[],
): { equipment: FilterOption[]; jobs: FilterOption[] } {
  const equipment = new Map<string, FilterOption>();
  for (const node of nodes) {
    equipment.set(node.id, { value: node.id, label: node.name, deleted: false });
  }
  for (const e of entries) {
    if (equipment.has(e.equipmentId)) continue;
    equipment.set(e.equipmentId, {
      value: e.equipmentId,
      label: e.equipmentName ?? e.equipmentCode ?? e.equipmentId,
      deleted: e.equipmentDeleted,
    });
  }
  const jobOptions = new Map<string, FilterOption>();
  for (const job of jobs) {
    jobOptions.set(job.id, {
      value: job.id,
      label: `${job.code} · ${job.title}`,
      deleted: job.deletedAt !== null,
    });
  }
  for (const e of entries) {
    if (!e.jobId || jobOptions.has(e.jobId)) continue;
    jobOptions.set(e.jobId, {
      value: e.jobId,
      label: [e.jobCode, e.jobTitle].filter(Boolean).join(' · ') || e.jobId,
      deleted: e.jobDeleted,
    });
  }
  const byLabel = (a: FilterOption, b: FilterOption) => a.label.localeCompare(b.label);
  return {
    equipment: [...equipment.values()].sort(byLabel),
    jobs: [...jobOptions.values()].sort(byLabel),
  };
}

/** Maintenance Plan'ın işaret süzgeçleri (adres: `?critical=1&class=1&overdue=1&postponed=1`). */
export interface PlanFlags {
  critical?: boolean;
  class?: boolean;
  overdue?: boolean;
  postponed?: boolean;
  /** UMS / E0–ECO alarm testi işleri (DNV-CP-0206 Sec.2 [4.3] d; ADR 0133 §3). */
  alarmTest?: boolean;
}

export const PLAN_FLAG_KEYS = ['critical', 'class', 'alarmTest', 'overdue', 'postponed'] as const;

/**
 * Planlı işler klas, kritik, gecikmiş ve ertelenmiş olarak süzülür [NR496 4.4.2b, 4.5.3e].
 * Nitelik işaretleri (kritik, klas) listeyi daraltır; durum işaretleri (gecikmiş, ertelenmiş)
 * kendi aralarında "ya da"dır: ikisi birlikte seçilince gecikmiş ya da ertelenmiş işler görünür,
 * liste boşalmaz (ortak 2026-10-25; ADR 0108 Ek). Klas kalemi ekipman ağacından ve işten
 * (`isClass`).
 */
export function filterPlanFlags<T extends Pick<PlanItem, 'critical' | 'status' | 'postponed'>>(
  plans: readonly T[],
  flags: PlanFlags,
  isClass: (plan: T) => boolean,
  isAlarmTest: (plan: T) => boolean = () => false,
): T[] {
  const byStatus = flags.overdue === true || flags.postponed === true;
  return plans.filter(
    (p) =>
      (!flags.critical || p.critical) &&
      (!flags.class || isClass(p)) &&
      (!flags.alarmTest || isAlarmTest(p)) &&
      (!byStatus ||
        (flags.overdue === true && p.status === 'overdue') ||
        (flags.postponed === true && p.postponed)),
  );
}

/**
 * Gecikmiş ya da ertelenmiş süzgeci seçiliyken plan tablosu dönemle sınırlanmaz (ortak
 * 2026-10-25; ADR 0108 Ek): bunlar istisna listesidir, ay, hafta ve liste görünümü aynı işleri
 * verir. Ertelenen işin yeni vadesi sonraki ayda, gecikenin vadesi önceki ayda kalsa da görünür.
 */
export function planFlagsSpanPeriods(flags: PlanFlags): boolean {
  return flags.overdue === true || flags.postponed === true;
}
